// ─── src/api/awardsNominations.ts ────────────────────────────────────────────
//
// Admin API for the Startup Syria Awards nomination inbox. All calls
// use the x-api-key `apiClient` — nominations never leave this dashboard.

import { apiClient } from "./registration";
import type { PaginationMeta } from "../types/registration";

// ── Types ────────────────────────────────────────────────────────────────────

export interface AwardsNomination {
  id: string;

  // Nominee
  category: string;
  nomineeName: string;
  nomineeRole: string;
  /** Legacy — 2026 form no longer collects these. Nullable on new rows. */
  nomineeEmail?: string | null;
  nomineePhone?: string | null;
  nomineeLinks?: string | null;

  // Nomination details
  nominationWhy: string;
  nominationAchievements: string;
  nominationStandout?: string | null;
  nominationSupport?: string | null;

  // Impact
  impactDescription: string;
  impactBeneficiaries?: string | null;
  impactReach?: string | null;

  // Nominator
  nominatorName: string;
  nominatorEmail: string;
  nominatorRelationship: string;
  anonymous: string;

  // Declarations
  declarationAccuracy: string;
  declarationContact: string;

  createdAt: string;
  updatedAt: string;
}

export interface AwardsNominationStats {
  total: number;
  anonymous: number;
  byCategory: Record<string, number>;
}

export interface AwardsNominationParams {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  anonymous?: string;
}

interface Paged<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

export interface NormalisedList<T> {
  data: T[];
  meta: PaginationMeta;
}

const normalise = <T,>(raw: Paged<T>): NormalisedList<T> => ({
  data: raw.data,
  meta: {
    total: raw.total,
    page: raw.page,
    limit: raw.limit,
    totalPages: Math.ceil(raw.total / raw.limit),
  },
});

const clean = <T extends object>(params: T) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, v]) => v !== undefined && v !== "" && v !== null,
    ),
  );

// ── Calls ────────────────────────────────────────────────────────────────────

export const getAwardsNominations = async (
  params: AwardsNominationParams,
): Promise<NormalisedList<AwardsNomination>> => {
  const { data } = await apiClient.get<Paged<AwardsNomination>>(
    "/awards-nominations",
    { params: clean(params) },
  );
  return normalise(data);
};

export const getAwardsNominationStats = async (): Promise<AwardsNominationStats> => {
  const { data } = await apiClient.get<AwardsNominationStats>(
    "/awards-nominations/stats",
  );
  return data;
};

export const deleteAwardsNomination = async (id: string): Promise<void> => {
  await apiClient.delete(`/awards-nominations/${id}`);
};
